import { Body, Col, IconButton, Row, Stack, SubHeading, useDialog } from '@workramp/ui';
import { useTheme } from 'styled-components';
import { DateTimeText } from '~/features/temporal';
import { I18n } from '~/common/explicit-exports';
import { ConfirmModal, type ConfirmModalProps } from '~/features/common';
import { StyledCard } from './OAuthConnectionCard.styles';

interface OAuthConnectionConfirmRevokeProps extends Omit<ConfirmModalProps, 'onCancel' | 'onConfirm' | 'title'> {
  onCancel: () => void;
  onConfirm: () => void;
  title: string;
  isOpen: boolean;
}

interface OAuthConnectionCardProps {
  applicationName: string;
  scopes: string[];
  createdAt: number;
  expiresAt?: number;
  id: number;
  revokedAt?: number;
  onRevoke?: (id: number, callback: () => void) => void;
}

export const OAuthConnectionCard = ({
  applicationName,
  createdAt,
  expiresAt,
  id,
  revokedAt,
  scopes,
  onRevoke,
}: OAuthConnectionCardProps) => {
  const theme = useTheme();
  const { open: openRevokeModal, close: closeRevokeModal } = useDialog<OAuthConnectionConfirmRevokeProps>(ConfirmModal);

  const CREATED = I18n.t('js.admin.integration_settings.oauth_connections.created');
  const EXPIRES = I18n.t('js.admin.integration_settings.oauth_connections.expires');
  const NO_EXPIRY = I18n.t('js.admin.integration_settings.oauth_connections.no_expiry');
  const REVOKE_CONNECTION = I18n.t('js.admin.integration_settings.oauth_connections.revoke_connection');
  const REVOKED = I18n.t('js.admin.integration_settings.oauth_connections.revoked');
  const SCOPES = I18n.t('js.admin.integration_settings.oauth_connections.scopes');
  const MODAL_ACTION = I18n.t('js.admin.integration_settings.oauth_connections.revoke_modal.confirmation_action');
  const MODAL_BODY = I18n.t('js.admin.integration_settings.oauth_connections.revoke_modal.body', {
    applicationName,
  });
  const MODAL_TITLE = I18n.t('js.admin.integration_settings.oauth_connections.revoke_modal.title');

  const handleRevoke = () => {
    openRevokeModal({
      title: MODAL_TITLE,
      body: MODAL_BODY,
      confirm: MODAL_ACTION,
      onConfirm: () => {
        onRevoke(id, closeRevokeModal);
      },
      onCancel: closeRevokeModal,
    });
  };

  const sortedScopes = scopes.slice();
  sortedScopes.sort();

  return (
    <StyledCard isDisabled={!!revokedAt}>
      <Row gapX={4} align="flex-start" justify="space-between">
        <Col>
          <Stack size={2}>
            <SubHeading level="md">{applicationName}</SubHeading>
            <Body level="md" color={theme.colors.on[600]}>
              {SCOPES}: {sortedScopes.join(', ')}
            </Body>
            <Body level="md" color={theme.colors.on[600]}>
              {CREATED}: <DateTimeText isLocalTime showWeekday dateStyle="long" date={createdAt} />
            </Body>
            {revokedAt ? (
              <Body level="md" color={theme.colors.danger[600]}>
                {REVOKED}: <DateTimeText isLocalTime showWeekday dateStyle="long" date={revokedAt} />
              </Body>
            ) : (
              <Body level="md" color={theme.colors.on[600]}>
                {EXPIRES}:{' '}
                {expiresAt ? <DateTimeText isLocalTime showWeekday dateStyle="long" date={expiresAt} /> : NO_EXPIRY}
              </Body>
            )}
          </Stack>
        </Col>
        {onRevoke && !revokedAt && (
          <Col xs="auto">
            <Row gapX={2}>
              <Col xs="auto">
                <IconButton
                  iconName="trash:regular"
                  onPress={handleRevoke}
                  label={REVOKE_CONNECTION}
                  variant="outline"
                  color="danger"
                />
              </Col>
            </Row>
          </Col>
        )}
      </Row>
    </StyledCard>
  );
};
